import { DatePicker } from "components/DatePicker/DatePicker";
import { format } from "date-fns";
import { Formik, type FormikHelpers } from "formik";
import { useAdmin } from "hooks/useAdmin";
import { useUserForm } from "hooks/useUserForm";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Heading3 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";

import {
  FormLabel,
  StyledButton,
  StyledField,
  StyledForm,
  StyledSelect,
} from "../../UserToolsPage.styled";
import type { DownloadUsersReportFormFields } from "./DownloadUsersReport.schema";

export const DownloadUsersReport: React.FC = observer(() => {
  const { t } = useTranslation("admin-environment");
  const { downloadUsersReport } = useAdmin();
  const { schools } = useUserForm();
  const formatDate = (date: Date | null): string | null => {
    return date ? format(date, "yyyy-MM-dd") : null;
  };

  const onFormSubmit = (
    {
      schoolId,
      startRegistrationDate,
      endRegistrationDate,
      startLastLoginDate,
      endLastLoginDate,
    }: DownloadUsersReportFormFields,
    { resetForm, setSubmitting }: FormikHelpers<DownloadUsersReportFormFields>,
  ) => {
    setSubmitting(true);
    downloadUsersReport(
      schoolId,
      formatDate(startRegistrationDate),
      formatDate(endRegistrationDate),
      formatDate(startLastLoginDate),
      formatDate(endLastLoginDate),
    ).finally(() => {
      setSubmitting(false);
      resetForm();
    });
  };

  const today = new Date();
  return (
    <Formik
      enableReinitialize
      initialValues={{
        schoolId: "",
        startRegistrationDate: null as Date | null,
        endRegistrationDate: null as Date | null,
        startLastLoginDate: null as Date | null,
        endLastLoginDate: null as Date | null,
      }}
      onSubmit={onFormSubmit}
    >
      {(formik) => (
        <StyledForm noValidate data-cy="download-users-report-form" onSubmit={formik.handleSubmit}>
          <Heading3>{t("adminTools.downloadUsersReport.title")}</Heading3>
          <ParagraphLarge>{t("adminTools.downloadUsersReport.description")}</ParagraphLarge>
          <StyledField>
            <FormLabel>
              {t(
                "adminTools.downloadUsersReport.dateInput.startRegistrationDate",
                "Start registration date of the users",
              )}
            </FormLabel>
            <DatePicker
              dataCy="start-registration-date-picker"
              value={formik.values.startRegistrationDate}
              maxDate={today}
              minDate={null}
              onChange={(date) => formik.setFieldValue("startRegistrationDate", date)}
            />
            <FormLabel>
              {t(
                "adminTools.downloadUsersReport.dateInput.endRegistrationDate",
                "End registration date of the users",
              )}
            </FormLabel>
            <DatePicker
              dataCy="end-registration-date-picker"
              value={formik.values.endRegistrationDate}
              maxDate={today}
              minDate={null}
              onChange={(date) => formik.setFieldValue("endRegistrationDate", date)}
            />
            <FormLabel>
              {t(
                "adminTools.downloadUsersReport.dateInput.startLastLoginDate",
                "Start last login date of the users",
              )}
            </FormLabel>
            <DatePicker
              dataCy="start-last-login-date-picker"
              value={formik.values.startLastLoginDate}
              maxDate={today}
              minDate={null}
              onChange={(date) => formik.setFieldValue("startLastLoginDate", date)}
            />
            <FormLabel>
              {t(
                "adminTools.downloadUsersReport.dateInput.endLastLoginDate",
                "End last login date of the users",
              )}
            </FormLabel>
            <DatePicker
              dataCy="end-last-login-date-picker"
              value={formik.values.endLastLoginDate}
              maxDate={today}
              minDate={null}
              onChange={(date) => formik.setFieldValue("endLastLoginDate", date)}
            />
          </StyledField>
          <StyledField>
            <label htmlFor="user-admin-form-school-selector">
              {t("accountDetails.school.label", "School")}
            </label>
            <StyledSelect
              showSearchBox
              aria-label={t("accountDetails.school.label", "School")}
              dataCy="users-report-account-school"
              id="users-report-form-school-selector"
              name="users-report-schoolId"
              options={schools}
              placeholder={t("accountDetails.school.defaultValue.label", "Select a school")}
              value={formik.values.schoolId}
              onChange={(event) => {
                formik.setFieldValue("schoolId", event.target.value);
              }}
            />
          </StyledField>
          <StyledButton
            dataCy="download-users-report-button"
            disabled={!!Object.keys(formik.errors).length || formik.isSubmitting}
            type="submit"
            variant="primary"
          >
            {t("adminTools.downloadUsersReport.buttons.download.label", "Download report")}
          </StyledButton>

          <StyledButton
            dataCy="clear-users-report-button"
            variant="secondary"
            onClick={(event) => {
              event.preventDefault();
              formik.resetForm();
            }}
          >
            {t("adminTools.downloadUsersReport.buttons.reset.label", "Reset form")}
          </StyledButton>
        </StyledForm>
      )}
    </Formik>
  );
});
